"use client"

import { useEffect, useState } from "react"
import { usePathname } from "next/navigation"
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  Box,
} from "@mui/material"
import { useGetIdentity } from "@refinedev/core"
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined"
import EmojiObjectsOutlinedIcon from "@mui/icons-material/EmojiObjectsOutlined"
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined"
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined"

// 账号级一次性提示 — storage key 绑定 userId,每个账号在每个浏览器看一次
const STORAGE_KEY_PREFIX = "ops:new-day-greeting-seen:"

// 限定路径:只在"账号数据"模块下弹(文案本身是举手/进单/成交业务语境,挂全局会违和)
const ENABLED_PATH_PREFIX = "/dashboard/operations/accounts"

type Identity = { id?: string; email?: string }
type DayKind = "monthStart" | "monday" | "normal"

function classifyDay(d: Date): DayKind {
  if (d.getDate() === 1) return "monthStart"
  if (d.getDay() === 1) return "monday"
  return "normal"
}

function GreetingRow({
  icon,
  title,
  desc,
}: {
  icon: React.ReactNode
  title: string
  desc: string
}) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box sx={{ color: "primary.main", display: "flex", mt: 0.25, flexShrink: 0 }}>
        {icon}
      </Box>
      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary" }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: "text.secondary", lineHeight: 1.7 }}>
          {desc}
        </Typography>
      </Stack>
    </Stack>
  )
}

export function NewDayGreetingDialog() {
  const pathname = usePathname()
  const { data: identity } = useGetIdentity<Identity>()
  const userId = identity?.id ?? identity?.email
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState<DayKind>("normal")
  const [today, setToday] = useState<Date>(new Date())

  useEffect(() => {
    if (!userId) return  // 还没拿到 identity,等下一次
    if (!pathname || !pathname.startsWith(ENABLED_PATH_PREFIX)) return  // 仅账号数据模块弹
    const storageKey = `${STORAGE_KEY_PREFIX}${userId}`
    let seen: string | null = null
    try {
      seen = window.localStorage.getItem(storageKey)
    } catch {
      return
    }
    if (seen === "1") return  // 该账号已看过 → 永不再弹
    const now = new Date()
    setToday(now)
    setKind(classifyDay(now))
    setOpen(true)
  }, [userId, pathname])

  const handleClose = () => {
    setOpen(false)
    if (!userId) return
    try {
      window.localStorage.setItem(`${STORAGE_KEY_PREFIX}${userId}`, "1")
    } catch {
      // 忽略 storage 写入错误
    }
  }

  // 文案 — 按日期类型分支(月首 > 周一 > 普通)
  const month = today.getMonth() + 1
  const dateLabel = `${month}/${today.getDate()}`
  const titleByKind = {
    monthStart: `${month} 月,新的开始`,
    monday: "新的一周开始了",
    normal: `${dateLabel} · 新的一天`,
  }
  const subtitleByKind = {
    monthStart: "上月已成定格,任何修正都回不到那个瞬间 — 但今天的 30 页还空着",
    monday: "7 天 7 次重新设定节奏的机会 — 上周已经写完,这一周等你下笔",
    normal: "昨天的判断已经无法改写,今天的判断还在你手上",
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <WbSunnyOutlinedIcon sx={{ fontSize: 24, color: "#f59e0b" }} />
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
            {titleByKind[kind]}
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 1, lineHeight: 1.7 }}>
          {subtitleByKind[kind]}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2.5}>
          {kind === "monthStart" ? (
            <>
              <GreetingRow
                icon={<RestartAltOutlinedIcon />}
                title="时间最公平的礼物"
                desc="每个月都是 30 天,差别在于谁先动笔 — 第一次举手、第一单进单、第一笔成交,都从今天起重写。"
              />
              <GreetingRow
                icon={<EmojiObjectsOutlinedIcon />}
                title="重要的从来不是某一天"
                desc="单日的举手 / 进单 / 成交是噪声,30 天累积出的曲线才是趋势 — 它告诉你方向,不评价瞬间。"
              />
              <GreetingRow
                icon={<InsightsOutlinedIcon />}
                title="小动作 · 大形状"
                desc="今天看似微不足道的一次举手,是月底那张漏斗图的一格刻度 — 30 天后,你才会看见今天的形状。"
              />
            </>
          ) : kind === "monday" ? (
            <>
              <GreetingRow
                icon={<EmojiObjectsOutlinedIcon />}
                title="节奏比速度更值钱"
                desc="稳定 5 天举手,胜过 1 天爆冲 — 平台权重奖励持续在岗的人,不是单日举手量的高峰。"
              />
              <GreetingRow
                icon={<InsightsOutlinedIcon />}
                title="上周已是历史"
                desc="上周的举手 / 进单 / 成交已成定格,无法改写 — 但能教你分辨:哪些时段的举手有效,哪些只是被噪音淹没。"
              />
            </>
          ) : (
            <>
              <GreetingRow
                icon={<EmojiObjectsOutlinedIcon />}
                title="专注本身就是产出"
                desc="今天每一次举手,都是在派单池里抢一格位置 — 看似零和,实际是 vBooking 排名权重的正向累积。"
              />
              <GreetingRow
                icon={<InsightsOutlinedIcon />}
                title="今天的判断 · 未来的证据"
                desc="今天每一笔进单 / 成交的录入,是明天复盘的素材、下个月排名上调的根据 — 数据不会忘记你。"
              />
            </>
          )}
        </Stack>

      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose} variant="contained" size="medium">
          开始今天
        </Button>
      </DialogActions>
    </Dialog>
  )
}
